import type { ChangeEvent } from 'react'
import { PatientUser } from '~/shared/types'
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useProgramEnrollmentEvent } from '../ProgramTabService'
import { FFModal } from '~/shared/ui'
import {
  listProgramEnrollmentsKey,
  PRIMARY_CARE_PROGRAM_UID,
  ProgramEnrollmentEvents,
} from '~/entities/program'
import {
  onboardingChurnedDischargedReasons,
  programsPrimaryCarePendingDischargeV2,
  ProgramsPrimaryCarePendingDischargeV2PartialUpdateBodyParams,
} from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'
import { useQuery } from '~/shared/hooks/query'
import { queueNotification } from '~/shared/notifications/actions'

interface ChurnMemberModelProps {
  patient: PatientUser
  handleClose: () => void
  open: boolean
  isChurned: boolean
}

const ChurnMemberModel: FC<ChurnMemberModelProps> = props => {
  const dispatch = useDispatch()
  const date = new Date()
  const [churnDate, setChurnDate] = useState<string>(
    `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`,
  )
  const [churnedReason, setChurnedReason] = useState<string | undefined>(undefined)
  const client = useQueryClient()
  const { mutateAsync: handleEnrollmentEvent } = useProgramEnrollmentEvent()
  // Reference data is effectively static for the session; keep cache warm so it
  // isn't refetched each time the modal re-mounts.
  const { data: churnedReasons } = useQuery(
    onboardingChurnedDischargedReasons.listQueryKey(),
    async () => (await onboardingChurnedDischargedReasons.list()).data,
    { staleTime: Infinity },
    { error: 'Failed to fetch churned reasons' },
  )

  const handleTextInputSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const dateStr: string = event.target.value
    setChurnDate(dateStr)
  }
  const handleChurn = async () => {
    try {
      // Below two calls to update onboarding status, we can remove this once we remove onboarding tab
      if (props.isChurned) {
        let reason = ''
        if (churnedReasons && churnedReason) {
          const selectedReason = churnedReasons.find(reason => reason.unique_key === churnedReason)
          reason = selectedReason ? `Churned:${selectedReason.label}` : ''
        }

        await handleEnrollmentEvent({
          uid: PRIMARY_CARE_PROGRAM_UID,
          event: ProgramEnrollmentEvents.PROGRAM_UNENROLLED,
          person_id: props.patient.person.id,
          event_date: churnDate,
          reason: reason,
        })
      } else {
        await programsPrimaryCarePendingDischargeV2.partialUpdate(
          { user_id: `${props.patient.id}` },
          {
            data: {
              pending_discharge_reason:
                churnedReason as ProgramsPrimaryCarePendingDischargeV2PartialUpdateBodyParams['pending_discharge_reason'],
            },
          },
        )
        await client.cancelQueries({
          queryKey: [listProgramEnrollmentsKey, props.patient.person.id],
        })
        await client.invalidateQueries({
          queryKey: [listProgramEnrollmentsKey, props.patient.person.id],
        })
      }
      props.handleClose()
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { pending_discharge_reason?: string[]; error?: string } }
      }
      const action = props.isChurned ? 'churn' : 'begin discharge for'
      const errorMessage =
        err?.response?.data?.pending_discharge_reason?.[0] ||
        err?.response?.data?.error ||
        `Failed to ${action} member`
      dispatch(queueNotification({ variant: 'error', message: errorMessage }))
    }
  }

  return (
    <FFModal open={props.open} style={{ width: '60%' }}>
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3, ml: 4 }}>
          <Typography variant="h6">
            {props.isChurned ? 'Churn Member' : 'Begin Discharge'}
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Autocomplete
              size="small"
              id="churn-reasons"
              value={churnedReasons?.find(reason => reason.unique_key === churnedReason) ?? null}
              onChange={(_, value) =>
                value?.unique_key ? setChurnedReason(value.unique_key) : setChurnedReason('')
              }
              options={churnedReasons ?? []}
              getOptionLabel={option => option.label}
              style={{ width: '70%' }}
              renderInput={params => (
                <TextField {...params} label="Select Reason" variant="outlined" />
              )}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              value={churnDate}
              onChange={handleTextInputSelection}
              label={props.isChurned ? 'Churn date' : 'Begin discharge on date'}
              variant="outlined"
              style={{ width: '70%' }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 4, py: 4 }}>
          <Box sx={{ mr: 1 }}>
            <Button disabled={false} onClick={props.handleClose} color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={false}
              variant="contained"
              color="primary"
              onClick={() => void handleChurn()}
            >
              {props.isChurned ? 'Churn' : 'Begin Discharge'}
            </Button>
          </Box>
        </Box>
      </Box>
    </FFModal>
  )
}

export default ChurnMemberModel
